import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cancelServiceOrder, applyCancellationFee } from '../../services/zohoServiceOrderService';
import { STANDARD_FEE_PRESETS, presetFee, presetLabel, useJobPrice, type StandardFeePreset } from '../../utils/cancellationFee';
import { useIonAlert } from '@ionic/react';
import {
  IonCard,
  IonAvatar,
  IonIcon,
  IonContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonItemGroup,
  IonItemDivider,
  IonText,
  IonRow,
  IonCol,
  IonSkeletonText,
  IonRefresherContent,
  useIonRouter,
} from '@ionic/react';

import {
  locationSharp,
  ellipsisHorizontal,
  createOutline,
  eyeOutline,
  calendarOutline,
  personOutline,
  closeCircleOutline
} from 'ionicons/icons';
import styles from './Agenda.module.scss';
import cancellationStyles from '../bookingSystem/CancellationModal.module.scss';
import type { AgendaProps as OriginalAgendaProps, BookingItem } from '@rsmaids/shared/types/bookings';
import SheetModal from '../../components/SheetModal';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import { getBookingPaymentStatus, getBookingPaymentStatusBatch } from '../../services/zohoInvoicingService';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import Booking from '../bookingSystem/BookingForm';
import BookingDetailsModal from '../bookingSystem/BookingDetails';
import BlockedTimeForm from './BlockedTimeForm';
import PositionedRefresher from '../../components/PositionedRefresher';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';
import { intlLocale } from '../../i18n/locale';



export interface AgendaProps extends OriginalAgendaProps {
  onLoadNextDays?: () => void;
  /** Loads the week before the earliest loaded day; called when the list is scrolled to the top. */
  onLoadPreviousDays?: () => void | Promise<void>;
  onDateChange?: (date: Date) => void;
  isAutoScrollingRef?: React.RefObject<boolean>;
  onRefresh?: () => Promise<void>;
  triggerNavigationSkeletonRef?: React.RefObject<(() => void) | null>;
  /** Bumped by the Today button: restart the list at the selected day, scrolled to its top, even when that day is
   *  already the selected one (the list is then scrolled down within it and the date doesn't change). */
  scrollToTopToken?: number;
}

function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

const Agenda: React.FC<AgendaProps> = ({
  selectedDate,
  isCalendarExpanded,
  bookings = [],
  completionStatuses = {},
  isLoading = false,
  onLoadNextDays,
  onLoadPreviousDays,
  onDateChange,
  isAutoScrollingRef,
  onRefresh,
  triggerNavigationSkeletonRef,
  scrollToTopToken,
}) => {
  const { t } = useTranslation();
  // IonRefresher only reaches native (icon-only, no content-push) mode when
  // it's decoupled from the element that actually scrolls — matching every
  // other working refresher in this codebase (ContactsList,
  // InboxConversationList, etc.), which attach IonRefresher to a
  // non-scrolling IonContent (scrollY=false) and scroll a plain div
  // alongside it instead of using IonContent's own built-in scroll. Agenda
  // previously scrolled via IonContent itself (scrollY=true), which kept
  // the refresher in classic content-push mode no matter what
  // pullingIcon/refreshingSpinner props were set.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const refresherAnchorRef = useRef<HTMLDivElement>(null);
  const [refresherReady, setRefresherReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setRefresherReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const lastDetectedDateRef = useRef<string | null>(null);
  const ionRouter = useIonRouter();
  const dataService = DataServiceFactory.getService();
  const [selectedBooking, setSelectedBooking] = useState<BookingItem | null>(null);

  // Payment status for bookings whose agenda item doesn't carry it (the
  // Firestore booking has no zohoPaymentStatus until something asks Zoho for
  // it). Looked up in one batch as the list loads — same pattern as
  // BookingsListModal/BookingsDesktop, and the batch call also writes the
  // result back to Firestore so later loads have it already — so the options
  // sheet badge is ready by the time it's opened instead of waiting on Zoho
  // at tap time.
  const [fetchedPayStatus, setFetchedPayStatus] = useState<Record<string, string>>({});
  const requestedPayIdsRef = useRef(new Set<string>());
  const payLookupAliveRef = useRef(true);
  useEffect(() => {
    payLookupAliveRef.current = true;
    return () => { payLookupAliveRef.current = false; };
  }, []);
  useEffect(() => {
    const ids = bookings
      .filter(b => b.type !== 'blocked' && b.status !== 'cancelled' && !b.zohoPaymentStatus && !requestedPayIdsRef.current.has(b.id))
      .map(b => b.id);
    if (!ids.length) return;
    ids.forEach(id => requestedPayIdsRef.current.add(id));

    const publish = (found: Record<string, string>) => {
      if (payLookupAliveRef.current && Object.keys(found).length) {
        setFetchedPayStatus((prev) => ({ ...prev, ...found }));
      }
    };

    (async () => {
      // Small batches, one after another. The server asks Zoho about every id in
      // a batch in parallel (up to 3 requests each); 30 at once gets some of
      // them dropped, which comes back as "unknown" — i.e. a false "No order".
      const CHUNK = 8;
      const unresolved: string[] = [];
      for (let i = 0; i < ids.length && payLookupAliveRef.current; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const found: Record<string, string> = {};
        try {
          const statuses = await getBookingPaymentStatusBatch(chunk);
          chunk.forEach((id) => {
            const status = (statuses[id]?.paymentStatus || '').toLowerCase();
            if (status && status !== 'unknown') found[id] = status;
            else unresolved.push(id);
          });
        } catch {
          unresolved.push(...chunk);
        }
        publish(found);
      }

      // Whatever the batch couldn't resolve: ask one at a time, the same call the
      // details screen makes. A booking with genuinely no order stops after one request.
      for (const id of unresolved) {
        if (!payLookupAliveRef.current) return;
        try {
          const status = ((await getBookingPaymentStatus(id)).paymentStatus || '').toLowerCase();
          if (status && status !== 'unknown') publish({ [id]: status });
        } catch { /* leave "No order" */ }
      }
    })();
  }, [bookings]);
  // Blocked time only carries a resource/staff ID, so resolve names once for display.
  const [staffNameById, setStaffNameById] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let cancelled = false;
    // Same resolution BlockedTimeForm uses: a blocked event points at a
    // schedule, and a schedule maps to staff via staff.scheduleIds or
    // schedule.externalId (= the staff member's resourceId).
    Promise.all([
      dataService.staff.getAllStaffMembers(),
      callAdminApi('querySchedules', 'bookings', {}).catch(() => ({})),
    ])
      .then(([list, schedulesResult]) => {
        if (cancelled) return;
        const map = new Map<string, string>();
        list.forEach((s) => {
          if (!s.name) return;
          if (s._id) map.set(s._id, s.name);
          if (s.resourceId) map.set(s.resourceId, s.name);
          (s.scheduleIds ?? []).forEach((sid) => { if (typeof sid === 'string') map.set(sid, s.name); });
        });
        const anySchedules = schedulesResult as Record<string, unknown>;
        const schedules = (Array.isArray(anySchedules.items) ? anySchedules.items : Array.isArray(anySchedules._items) ? anySchedules._items : []) as Array<Record<string, unknown>>;
        schedules.forEach((sch) => {
          const sid = typeof sch._id === 'string' ? sch._id : '';
          const ext = typeof sch.externalId === 'string' ? sch.externalId : '';
          const name = ext ? map.get(ext) : undefined;
          if (sid && name && !map.has(sid)) map.set(sid, name);
        });
        setStaffNameById(map);
      })
      .catch((err) => console.error('[Agenda] failed to load staff names', err));
    return () => { cancelled = true; };
  }, [dataService]);
  const [isBlockedFormOpen, setIsBlockedFormOpen] = useState(false);
  const [selectedBlockedBooking, setSelectedBlockedBooking] = useState<BookingItem | null>(null);
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [noMoreBookings, setNoMoreBookings] = useState(false);
  const [isNavigatingDateRange, setIsNavigatingDateRange] = useState(false);
  const prevBookingsLengthRef = useRef(bookings.length);
  // isLoadingMore (state) has a real gap: scroll events fire far faster than
  // React can re-render and rebind handleScroll with an updated closure, so
  // several scroll events in a row can each read the same stale
  // isLoadingMore=false before any of them commits setIsLoadingMore(true) —
  // confirmed by duplicate onLoadNextDays() calls for the identical week in
  // the console. A ref is checked/set synchronously and isn't subject to
  // that render-cycle lag, so it actually serializes the calls.
  const isLoadingMoreRef = useRef(false);

  // Booking modal state
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingModalId, setBookingModalId] = useState<string | undefined>(undefined);

  // Booking details modal state
  const [isBookingDetailsModalOpen, setIsBookingDetailsModalOpen] = useState(false);
  const [bookingDetailsModalId, setBookingDetailsModalId] = useState<string | undefined>(undefined);

  const [presentAlert] = useIonAlert();
  // Alert state - single global state for all alerts
  const [activeAlert, setActiveAlert] = useState<'cancellation-options' | null>(null);
  const [waiveFee, setWaiveFee] = useState(true);
  const [notifyClient, setNotifyClient] = useState(true);
  const [cancellationMessage, setCancellationMessage] = useState('');
  const [feeStep, setFeeStep] = useState<'selection' | 'type'>('selection');
  const [feeType, setFeeType] = useState<'standard' | 'custom' | null>(null);
  const [customFeeAmount, setCustomFeeAmount] = useState('');
  const [standardPreset, setStandardPreset] = useState<StandardFeePreset | null>(null);
  const jobPrice = useJobPrice(selectedBooking?.id, feeType === 'standard');

  const recentlyAutoScrolledRef = useRef(false);
  const lastUserScrollTimeRef = useRef<number>(0);
  const isBookingModalOpenRef = useRef(false);
  const isBookingDetailsModalOpenRef = useRef(false);
  // Lets the mount-only storage-listener effect below always call the
  // latest onRefresh/ionRouter without needing to re-subscribe the listener
  // whenever either identity changes.
  const onRefreshRef = useRef(onRefresh);
  const ionRouterRef = useRef(ionRouter);
  useEffect(() => {
    onRefreshRef.current = onRefresh;
    ionRouterRef.current = ionRouter;
  }, [onRefresh, ionRouter]);
  // loadCompletionStatuses is declared below (it's a useCallback that
  // depends on `bookings`); this ref lets the mount-only listener effect
  // above call the current version without re-subscribing on every
  // bookings change.
  const loadCompletionStatusesRef = useRef<() => void>(() => { });

  // Keep refs in sync with state
  useEffect(() => {
    console.log('isBookingModalOpen changed to:', isBookingModalOpen);
    isBookingModalOpenRef.current = isBookingModalOpen;
  }, [isBookingModalOpen]);

  useEffect(() => {
    console.log('isBookingDetailsModalOpen changed to:', isBookingDetailsModalOpen);
    isBookingDetailsModalOpenRef.current = isBookingDetailsModalOpen;
  }, [isBookingDetailsModalOpen]);

  useEffect(() => {
    loadCompletionStatusesRef.current();

    // Storage listener - only react to specific triggers
    const removeListener = DataStorageService.setupStorageListener((key) => {
      // Only log and process specific keys to avoid spam from scroll-related storage
      if (key === 'booking-refresh-trigger') {
        // Complete asks for no reload (see BookingDetails.handleStatusChange)
        const skipAt = DataStorageService.getData<number>('booking-refresh-skip-agenda');
        if (skipAt && Date.now() - skipAt < 3000) return;
        setIsRefreshing(true);
        if (onRefreshRef.current) {
          onRefreshRef.current().finally(() => {
            setIsRefreshing(false);
          });
        } else {
          setTimeout(() => {
            setIsRefreshing(false);
          }, 1500);
        }
      } else if (key === 'open-booking-modal') {
        const bookingId = DataStorageService.getData<string>('open-booking-modal');
        console.log('open-booking-modal event:', {
          bookingId,
          isBookingModalOpenRef: isBookingModalOpenRef.current,
          isBookingDetailsModalOpenRef: isBookingDetailsModalOpenRef.current
        });
        // Only open if the booking modal is not already open OR being opened
        if (bookingId && !isBookingModalOpenRef.current) {
          DataStorageService.removeData('open-booking-modal');
          console.log('Opening booking modal from storage event');
          setBookingModalId(bookingId);
          setIsBookingModalOpen(true);
        } else if (bookingId) {
          // Clear the storage key anyway to prevent it from triggering again
          console.log('Booking modal already open or opening, clearing storage key');
          DataStorageService.removeData('open-booking-modal');
        }
      } else if (key === 'edit-booking-modal') {
        const bookingId = DataStorageService.getData<string>('edit-booking-modal');
        if (bookingId && !isBookingModalOpenRef.current) {
          DataStorageService.removeData('edit-booking-modal');
          setBookingModalId(bookingId);
          setIsBookingModalOpen(true);
        }
      } else if (key === 'navigate-to-contact') {
        const contactId = DataStorageService.getData<string>('navigate-to-contact');
        if (contactId) {
          DataStorageService.removeData('navigate-to-contact');
          ionRouterRef.current.push(`/tabs/contact/${contactId}`, 'forward', 'push');
        }
      } else if (key === 'navigate-to-inbox') {
        DataStorageService.removeData('navigate-to-inbox');
        ionRouterRef.current.push('/tabs/inbox', 'forward', 'push');
      } else if (key.startsWith('navigate-to-')) {
        // Handle other navigation triggers from booking details modal
        const destination = key.replace('navigate-to-', '');
        const bookingId = DataStorageService.getData<string>(key);
        DataStorageService.removeData(key);

        if (destination === 'order' && bookingId) {
          ionRouterRef.current.push(`/tabs/order/${bookingId}`, 'forward', 'push');
        } else if (destination === 'invoice' && bookingId) {
          ionRouterRef.current.push(`/tabs/invoice/${bookingId}`, 'forward', 'push');
        } else if (destination === 'notes' && bookingId) {
          ionRouterRef.current.push(`/tabs/notes/${bookingId}`, 'forward', 'push');
        }
      } else if (key.startsWith('booking-') && key.endsWith('-completed')) {
        // Handle completion status updates silently
        loadCompletionStatusesRef.current();
      }
      // Ignore all other storage events (like scroll position, booking data, etc.)
    });

    return () => {
      removeListener();
    };
  }, []);

  const loadCompletionStatuses = useCallback(() => {
    if (bookings && bookings.length > 0) {
      const statuses: Record<string, boolean> = {};
      bookings.forEach((booking: BookingItem) => {
        if (booking.id) {
          statuses[booking.id] = DataStorageService.getData<boolean>(`booking-${booking.id}-completed`) || false;
        }
      });
      setCompletionStatus(statuses);
    }
  }, [bookings]);
  useEffect(() => {
    loadCompletionStatusesRef.current = loadCompletionStatuses;
  }, [loadCompletionStatuses]);

  // Memoize avatar URLs to prevent recalculation during scroll
  const avatarUrls = React.useMemo(() => {
    const urls: Record<string, string> = {};
    if (bookings) {
      bookings.forEach(booking => {
        const contact = booking.contact;
        if (!contact) {
          urls[booking.id] = getDefaultAvatar('Client');
        } else {
          const avatarUrl = contact.avatarUrl;
          if (avatarUrl) {
            const proxiedUrl = proxyImage(avatarUrl);
            urls[booking.id] = proxiedUrl || getDefaultAvatar(contact.name || 'Client');
          } else {
            urls[booking.id] = getDefaultAvatar(contact.name || 'Client');
          }
        }
      });
    }
    return urls;
  }, [bookings]);

  const handleBookingClick = (booking: BookingItem, dayDate: Date) => {
    const enrichedbooking = {
      ...booking,
      date: dayDate.toISOString().split('T')[0]
    };

    DataStorageService.storeData(
      `booking-${booking.id}`,
      enrichedbooking
    );

    // Open booking details modal instead of navigating
    setBookingDetailsModalId(booking.id);
    setIsBookingDetailsModalOpen(true);
  };

  const checkCompletionStatus = useCallback((bookingId: string) => {
    return completionStatuses?.[bookingId] || false;
  }, [completionStatuses]);

  const handleBookAgain = useCallback((booking: BookingItem) => {
    if (booking && booking.contact) {
      const contactData = {
        id: booking.contact.id,
        name: booking.contact.name,
        email: booking.contact.email,
        phone: booking.contact.phone,
        avatarUrl: booking.contact.avatarUrl
      };

      DataStorageService.storeData('selectedContact', contactData);
      DataStorageService.storeData('bookingSelectedDate', selectedDate);

      // Open the booking modal for a new booking instead of navigating
      setBookingModalId('new');
      setIsBookingModalOpen(true);
    }
  }, [selectedDate]);

  const closeOptionsModal = useCallback(() => {
    setSelectedBooking(null);
  }, []);

  const getBookingPaymentBadge = (booking: BookingItem): { label: string; color: string } => {
    const payStatus = booking.zohoPaymentStatus ?? fetchedPayStatus[booking.id];
    if (payStatus === 'paid') return { label: t('pages.bookingCalendar.Agenda.paid'), color: 'success' };
    if (payStatus === 'overdue') return { label: t('pages.bookingCalendar.Agenda.overdue'), color: 'danger' };
    if (payStatus === 'unpaid') return { label: t('pages.bookingCalendar.Agenda.unpaid'), color: 'warning' };
    if (payStatus === 'partially_paid') return { label: t('pages.bookingCalendar.Agenda.partial'), color: 'warning' };
    if (payStatus === 'sent' || payStatus === 'viewed') return { label: t('pages.bookingCalendar.Agenda.sent'), color: 'primary' };
    if (payStatus === 'draft') return { label: t('pages.bookingCalendar.Agenda.draft'), color: 'medium' };
    if (booking.serviceType === 'Free Estimate') return { label: t('pages.bookingCalendar.Agenda.free'), color: 'tertiary' };
    return { label: t('pages.bookingCalendar.Agenda.noOrder'), color: 'medium' };
  };

  const formatBookingHeaderSubtitle = (booking: BookingItem): string => {
    const start = toDate(booking.startTime);
    const end = toDate(booking.endTime);
    const dateStr = start.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
    const timeStr = start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    const totalMins = Math.round((end.getTime() - start.getTime()) / 60000);
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    const duration = hrs > 0
      ? `${hrs}hr${hrs > 1 ? 's' : ''}${mins > 0 ? ` ${mins}mins` : ''}`
      : `${mins}mins`;

    return `${dateStr}, ${timeStr} (${duration})`;
  };

  const getbookingOptions = useCallback((booking: BookingItem) => [
    {
      label: t('pages.bookingCalendar.Agenda.viewDetails'),
      icon: eyeOutline,
      onClick: () => {
        if (!booking) return;

        const enrichedbooking = {
          ...booking,
          date: selectedDate?.toISOString().split('T')[0]
        };

        DataStorageService.storeData(
          `edit-booking-${booking.id}`,
          enrichedbooking
        );

        closeOptionsModal();

        // Open the booking details modal instead of navigating
        setBookingDetailsModalId(booking.id);
        setIsBookingDetailsModalOpen(true);
      }
    },
    {
      label: t('pages.bookingCalendar.Agenda.editBooking'),
      icon: createOutline,
      onClick: () => {
        if (!booking) return;

        const enrichedbooking = {
          ...booking,
          date: selectedDate?.toISOString().split('T')[0]
        };

        DataStorageService.storeData(
          `edit-booking-${booking.id}`,
          enrichedbooking
        );

        closeOptionsModal();

        // Open the booking modal instead of navigating
        setBookingModalId(booking.id);
        setIsBookingModalOpen(true);
      }
    },
    {
      label: t('pages.bookingCalendar.Agenda.bookAgain'),
      icon: calendarOutline,
      onClick: () => {
        handleBookAgain(booking);
        closeOptionsModal();
      }
    },
    {
      label: t('pages.bookingCalendar.Agenda.viewContact'),
      icon: personOutline,
      onClick: () => {
        if (!booking.contact?.id) return;

        DataStorageService.storeData(
          `contact-${booking.contact.id}`,
          booking.contact
        );

        closeOptionsModal();
        ionRouter.push(`/tabs/contact/${booking.contact.id}`, 'forward', 'push');
      }
    },
    {
      label: t('pages.bookingCalendar.Agenda.cancelBooking'),
      icon: closeCircleOutline,
      color: 'danger',
      onClick: () => {
        // Don't close options modal yet - we need selectedBooking for the cancellation form
        setActiveAlert('cancellation-options');
      }
    }
  ], [handleBookAgain, closeOptionsModal, selectedDate, ionRouter, t]);

  function toDate(val: string | Date): Date {
    if (val instanceof Date) return val;
    return new Date(val);
  }

  function getLocalDateString(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const userIsScrollingRef = useRef(false);

  // True while the list is moving (touch, momentum, wheel) and for a moment after. While it
  // is, nothing may replace or restart the list — loading more only adds content.
  const [isScrollActive, setIsScrollActive] = useState(false);
  const scrollActiveRef = useRef(false);
  const programmaticScrollUntilRef = useRef(0);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let idleTimeout: NodeJS.Timeout;
    const onScrollActivity = () => {
      // Scrolls the app does itself (restarting at a picked date, scrolling to the selected
      // day) are not the user scrolling — counting them held the skeleton back for a
      // moment, so Today showed the list, then the skeleton, then the list again.
      if (Date.now() < programmaticScrollUntilRef.current || isAutoScrollingRef?.current) return;
      scrollActiveRef.current = true;
      setIsScrollActive(true);
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        scrollActiveRef.current = false;
        setIsScrollActive(false);
      }, 300);
    };
    el.addEventListener('scroll', onScrollActivity, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScrollActivity);
      clearTimeout(idleTimeout);
    };
  }, [isAutoScrollingRef]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let scrollTimeout: NodeJS.Timeout;
    const setUserScrolling = () => {
      userIsScrollingRef.current = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        userIsScrollingRef.current = false;
      }, 500);
    };
    el.addEventListener('touchstart', setUserScrolling, { passive: true });
    el.addEventListener('pointerdown', setUserScrolling, { passive: true });
    el.addEventListener('mousedown', setUserScrolling, { passive: true });
    el.addEventListener('wheel', setUserScrolling, { passive: true });
    return () => {
      el.removeEventListener('touchstart', setUserScrolling);
      el.removeEventListener('pointerdown', setUserScrolling);
      el.removeEventListener('mousedown', setUserScrolling);
      el.removeEventListener('wheel', setUserScrolling);
      clearTimeout(scrollTimeout);
    };
  }, []);


  // First day shown in the list ('YYYY-MM-DD'); null = every loaded day. A date picked by
  // a tap, swipe or the year picker starts the list AT that date (nothing above it, so
  // nothing has to scroll); scrolling up past the top then reveals earlier days.
  const dateFromScrollRef = useRef(new Map<string, number>());
  // True from a user-chosen date (or the first load) until loading stops — see showSkeleton
  const [explicitLoad, setExplicitLoad] = useState(true);
  const [listFloor, setListFloor] = useState<string | null>(() => getLocalDateString(selectedDate));
  const prevSelectedStrRef = useRef(getLocalDateString(selectedDate));
  // The day whose selection was just reported by this list's own scrolling. The scroll-to-the-selected-day effect
  // below skips that one change: the list is already where the user scrolled it, and snapping to the day's heading
  // moved it up ~27px each time the user paused while more days were loading.
  const scrollOriginatedSelectionRef = useRef('');
  React.useLayoutEffect(() => {
    const selectedStr = getLocalDateString(selectedDate);
    // Same day handed down again as a new Date object (it happens whenever the parent
    // re-sets the date): not a new selection, so leave the list exactly as it is.
    if (selectedStr === prevSelectedStrRef.current) return;
    prevSelectedStrRef.current = selectedStr;
    // dateFromScrollRef is set only by this list's own scroll tracking, just before it
    // reports the new day — so it identifies a selection that came from scrolling.
    // (lastDetectedDateRef can't be used: it goes stale and can equal a later pick, such
    // as today, which then wrongly kept every earlier loaded day above it.)
    // Several days can be reported before React renders any of them (fast scrolling), so
    // this is a short-lived set of reported days rather than a single value.
    const reportedAt = dateFromScrollRef.current.get(selectedStr);
    // Never restart the list while the user's finger is on it / it is still being scrolled:
    // scrolling only ever adds content, it must not refresh or jump the list.
    const cameFromScroll =
      scrollActiveRef.current || userIsScrollingRef.current || (reportedAt !== undefined && Date.now() - reportedAt < 1500);
    dateFromScrollRef.current.delete(selectedStr);
    if (cameFromScroll) scrollOriginatedSelectionRef.current = selectedStr;
    if (!cameFromScroll) {
      setListFloor(selectedStr);
      setExplicitLoad(true);
      // The picked day becomes the first item, so go back to the very top. Without this the
      // old scroll position survives the list change and the day's items end up scrolled
      // out of view above its (sticky) header.
      programmaticScrollUntilRef.current = Date.now() + 800;
      scrollActiveRef.current = false;
      if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    }
  }, [selectedDate]);

  // The Today button while today is already selected: the date doesn't change, so the effect above never restarts the
  // list. When the button's token changes, start the list at the selected day and scroll to its top, the same as a
  // picked date does.
  const prevScrollToTopTokenRef = useRef(scrollToTopToken);
  React.useLayoutEffect(() => {
    if (scrollToTopToken === prevScrollToTopTokenRef.current) return;
    prevScrollToTopTokenRef.current = scrollToTopToken;
    setListFloor(getLocalDateString(selectedDate));
    programmaticScrollUntilRef.current = Date.now() + 800;
    scrollActiveRef.current = false;
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [scrollToTopToken, selectedDate]);

  const isLoadingPreviousRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  // finishLoadMore is defined further down; handlers above reach it through this ref
  const finishLoadMoreRef = useRef<() => void>(() => { });
  // Scroll metrics from just before earlier days were loaded, so the list can be
  // kept where the user was once they're added above.
  const previousLoadAnchorRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);

  const handleScroll = useCallback(() => {
    if (isAutoScrollingRef?.current) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    // Scrolled UP to near the top by the user: load the week before the earliest loaded
    // day. Only when moving up — a list that has just started at its first day sits at the
    // top, and scrolling down from there must not trigger (and re-render) anything.
    const scrolledUp = scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    if (onLoadPreviousDays && scrolledUp && scrollTop < 150 && userIsScrollingRef.current && !isLoadingPreviousRef.current) {
      isLoadingPreviousRef.current = true;
      previousLoadAnchorRef.current = { scrollTop, scrollHeight };
      Promise.resolve(onLoadPreviousDays()).finally(() => {
        // reveal the earlier days now that they are loaded
        setListFloor(null);
        setTimeout(() => {
          isLoadingPreviousRef.current = false;
          previousLoadAnchorRef.current = null;
        }, 500);
      });
    }

    if (!onLoadNextDays) return;
    if (noMoreBookings && userIsScrollingRef.current && scrollTop + clientHeight < scrollHeight - 20) {
      setNoMoreBookings(false);
    }
    // Was scrollHeight - 1 — required scrolling to within a single pixel of
    // the true bottom, which fractional/sub-pixel scroll metrics on real
    // devices (and the ~90px bottom padding added to clear the tab bar)
    // could make effectively unreachable, silently preventing "load more"
    // from ever firing on scroll.
    // Start loading once the spinner row (the list's last row, just above the tab-bar
    // clearance) has been scrolled into view, so the spinner is visible while it loads.
    if (!noMoreBookings && scrollTop + clientHeight >= scrollHeight - 24 && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      prevBookingsLengthRef.current = bookings.length;
      Promise.resolve(onLoadNextDays()).finally(() => {
        setTimeout(() => finishLoadMoreRef.current(), 500);
      });
    }
  }, [onLoadNextDays, onLoadPreviousDays, bookings, isAutoScrollingRef, noMoreBookings]);

  // Earlier days were just added above the list: shift the scroll position down by
  // the height they added so the content the user was looking at doesn't jump.
  React.useLayoutEffect(() => {
    const anchor = previousLoadAnchorRef.current;
    const el = scrollContainerRef.current;
    if (!anchor || !el) return;
    const added = el.scrollHeight - anchor.scrollHeight;
    if (added > 0) {
      el.scrollTop = anchor.scrollTop + added;
      previousLoadAnchorRef.current = null;
    }
  }, [bookings, listFloor]);

  const visibleBookings = React.useMemo(() => {
    const filtered = bookings.filter(b => {
      const status = String(b.status).toLowerCase();
      // Filter out all variations of cancelled/canceled status
      return status !== 'canceled' && status !== 'cancelled';
    });
    return filtered;
  }, [bookings]);

  const getSortedUniqueBookingDates = useCallback((_bookings: BookingItem[], includePast = false) => {
    const today = new Date();
    const todayStr = getLocalDateString(today);
    const dateSet = new Set(visibleBookings.map(b => getLocalDateString(toDate(b.startTime))));
    let dates = Array.from(dateSet).sort();
    if (!includePast) {
      dates = dates.filter(date => date >= todayStr);
    }
    return dates;
  }, [visibleBookings]);

  const getCurrentWeekUniqueDays = useCallback(() => {
    const selectedDateStr = getLocalDateString(selectedDate);
    const allDates = getSortedUniqueBookingDates(visibleBookings, true);
    function getStartOfWeek(dateStr: string) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      const start = new Date(date);
      start.setDate(date.getDate() - dayOfWeek);
      return getLocalDateString(start);
    }
    const weekStartDateStr = getStartOfWeek(selectedDateStr);
    const weekDates = allDates.filter(date => date >= weekStartDateStr);
    const weekDatesLimited = weekDates.filter(date => {
      const d = new Date(date);
      const start = new Date(weekStartDateStr);
      return (d.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) < 7;
    });
    return weekDatesLimited.length;
  }, [selectedDate, visibleBookings, getSortedUniqueBookingDates]);

  const getLastVisibleDateOfWeek = useCallback(() => {
    const selectedDateStr = getLocalDateString(selectedDate);
    const allDates = getSortedUniqueBookingDates(visibleBookings, true);
    function getStartOfWeek(dateStr: string) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay();
      const start = new Date(date);
      start.setDate(date.getDate() - dayOfWeek);
      return getLocalDateString(start);
    }
    const weekStartDateStr = getStartOfWeek(selectedDateStr);
    const weekDates = allDates.filter(date => date >= weekStartDateStr);
    const weekDatesLimited = weekDates.filter(date => {
      const d = new Date(date);
      const start = new Date(weekStartDateStr);
      return (d.getTime() - start.getTime()) / (1000 * 60 * 60 * 24) < 7;
    });
    return weekDatesLimited[weekDatesLimited.length - 1];
  }, [selectedDate, visibleBookings, getSortedUniqueBookingDates]);

  useEffect(() => {
    if (!onLoadNextDays || noMoreBookings || isLoadingMoreRef.current) return;
    const selectedDateStr = getLocalDateString(selectedDate);
    const hasVisibleBookings = bookings.some(b => getLocalDateString(toDate(b.startTime)) === selectedDateStr);
    if (!hasVisibleBookings) return;
    const weekDays = getCurrentWeekUniqueDays();
    const lastVisibleDate = getLastVisibleDateOfWeek();
    if (weekDays <= 5) return;
    if (selectedDateStr !== lastVisibleDate) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1 && !isLoadingMoreRef.current) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      prevBookingsLengthRef.current = bookings.length;
      Promise.resolve(onLoadNextDays()).finally(() => {
        setTimeout(() => finishLoadMoreRef.current(), 500);
      });
    }
  }, [bookings, onLoadNextDays, noMoreBookings, isLoadingMore, selectedDate, getCurrentWeekUniqueDays, getLastVisibleDateOfWeek]);

  // "No more bookings" is decided once, when a load finishes without having added
  // anything (see finishLoadMore). It used to be re-derived in an effect on every change of
  // `bookings`, and `bookings` changes identity whenever the selected date moves — which
  // flagged "no more" while scrolling and blocked loading (and the spinner) entirely.
  const bookingsLengthRef = useRef(bookings.length);
  useEffect(() => {
    bookingsLengthRef.current = bookings.length;
  }, [bookings]);

  const finishLoadMore = useCallback(() => {
    isLoadingMoreRef.current = false;
    setIsLoadingMore(false);
    if (bookingsLengthRef.current === prevBookingsLengthRef.current && bookingsLengthRef.current > 0) {
      setNoMoreBookings(true);
    }
  }, []);
  useEffect(() => {
    finishLoadMoreRef.current = finishLoadMore;
  }, [finishLoadMore]);

  // Reset the "no more bookings" flag whenever a refresh happens —
  // computed during render (React's "adjust state when a dependency
  // changes" pattern); the ref reset stays in the effect below since
  // reading/writing refs during render is its own anti-pattern.
  const [prevIsRefreshingForReset, setPrevIsRefreshingForReset] = useState(isRefreshing);
  if (prevIsRefreshingForReset !== isRefreshing) {
    setPrevIsRefreshingForReset(isRefreshing);
    setNoMoreBookings(false);
  }
  useEffect(() => {
    isLoadingMoreRef.current = false;
  }, [isRefreshing]);

  useEffect(() => {
    // This run was caused by a date the list's own scrolling just reported: leave the list where the user scrolled it.
    // (Consumed here, before the loading check, so only this one change is skipped — a pick in the calendar, a refresh
    // and the first load still scroll to the selected day.)
    if (scrollOriginatedSelectionRef.current === getLocalDateString(selectedDate)) {
      scrollOriginatedSelectionRef.current = '';
      return;
    }
    if (isLoading || isRefreshing) return;
    let autoScrollTimeout: NodeJS.Timeout;
    let observer: MutationObserver | null = null;
    const now = Date.now();
    const doAutoScroll = () => {
      const selectedDateStr = getLocalDateString(selectedDate);
      lastDetectedDateRef.current = selectedDateStr;
      let retries = 0;
      const maxRetries = 20;
      const tryScroll = () => {
        const divider = document.querySelector(`[data-date="${selectedDateStr}"]`);
        if (divider) {
          if (observer) observer.disconnect();
          if (isAutoScrollingRef) isAutoScrollingRef.current = true;
          recentlyAutoScrolledRef.current = true;

          const releaseGuard = () => {
            if (isAutoScrollingRef) isAutoScrollingRef.current = false;
            recentlyAutoScrolledRef.current = false;
          };

          // Compute the divider's target scrollTop ourselves instead of using
          // scrollIntoView — inside Ionic's custom-scrolling ion-content that
          // has undershot the target, leaving the previous day's last row
          // still peeking above "Today". Poll against this known target (not
          // just "scrollTop stopped changing") so the guard releases exactly
          // when the scroll actually arrives, with a timed fallback as a
          // safety net.
          const el = scrollContainerRef.current;
          if (!el) { releaseGuard(); return; }
          const containerRect = el.getBoundingClientRect();
          const dividerRect = divider.getBoundingClientRect();
          // getBoundingClientRect() is in on-screen (transformed) pixels. The year picker
          // zooms the calendar page back in while this runs (on a phone the zoom is still
          // in progress), so convert the distance back to the list's own pixels or the
          // scroll lands in the wrong place.
          const pageScale = containerRect.height / (el.clientHeight || 1) || 1;
          const targetScrollTop = Math.max(0, el.scrollTop + (dividerRect.top - containerRect.top) / pageScale);
          el.scrollTo({ top: targetScrollTop, behavior: 'auto' });

          let totalChecks = 0;
          const poll = () => {
            totalChecks++;
            if (Math.abs(el.scrollTop - targetScrollTop) <= 2 || totalChecks >= 40) {
              releaseGuard();
              return;
            }
            setTimeout(poll, 50);
          };
          setTimeout(poll, 50);
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(tryScroll, 100);
        } else {
          // Fallback: observe DOM for divider
          observer = new MutationObserver(() => {
            const dividerNow = document.querySelector(`[data-date="${selectedDateStr}"]`);
            if (dividerNow) {
              dividerNow.scrollIntoView({ behavior: 'auto', block: 'start' });
              if (observer) observer.disconnect();
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(tryScroll);
      });
    };
    if (now - lastUserScrollTimeRef.current < 1000) {
      autoScrollTimeout = setTimeout(() => {
        if (Date.now() - lastUserScrollTimeRef.current >= 1000) {
          doAutoScroll();
        }
      }, 1000 - (now - lastUserScrollTimeRef.current));
    } else {
      doAutoScroll();
    }
    return () => {
      if (autoScrollTimeout) clearTimeout(autoScrollTimeout);
      if (observer) observer.disconnect();
    };
  }, [selectedDate, isAutoScrollingRef, isLoading, isRefreshing]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let scrollTimeout: NodeJS.Timeout;
    let lastDetectAt = 0;
    // While scrolling, check which day is at the top at most this often (and once
    // more when the scrolling stops), so the calendar's selected date follows the
    // list live instead of only catching up after it stops.
    const DETECT_INTERVAL_MS = 80;

    const detectTopDay = () => {
      lastDetectAt = Date.now();
      {
        const containerRect = el.getBoundingClientRect();
        const dayDividers: HTMLElement[] = Array.from(document.querySelectorAll('ion-item-divider[data-date]'));
        if (dayDividers.length === 0) return;

        let targetDivider: HTMLElement | null = null;
        for (let i = 0; i < dayDividers.length; i++) {
          const rect = dayDividers[i].getBoundingClientRect();
          if (rect.top <= containerRect.top) {
            targetDivider = dayDividers[i];
          } else {
            break;
          }
        }
        if (!targetDivider && dayDividers.length > 0) {
          targetDivider = dayDividers[0];
        }
        const targetDate = targetDivider ? targetDivider.getAttribute('data-date') : null;

        if (targetDate && onDateChange) {
          if (targetDate !== lastDetectedDateRef.current && targetDate !== getLocalDateString(selectedDate)) {
            lastDetectedDateRef.current = targetDate;
            const date = parseLocalDate(targetDate);
            if (!isNaN(date.getTime())) {
              dateFromScrollRef.current.set(targetDate, Date.now());
              onDateChange(date);
            }
          }
        }
      }
    };

    const handleUserScroll = () => {
      if (isAutoScrollingRef?.current || recentlyAutoScrolledRef.current) {
        return;
      }

      lastUserScrollTimeRef.current = Date.now();

      clearTimeout(scrollTimeout);
      if (Date.now() - lastDetectAt >= DETECT_INTERVAL_MS) {
        detectTopDay();
      } else {
        // trailing check, so the final position is always picked up
        scrollTimeout = setTimeout(detectTopDay, DETECT_INTERVAL_MS);
      }
    };

    el.addEventListener('scroll', handleUserScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleUserScroll);
      clearTimeout(scrollTimeout);
    };
  }, [onDateChange, isAutoScrollingRef, selectedDate]);

  const handleRefresh = useCallback(async (event: CustomEvent) => {
    try {
      setIsRefreshing(true);

      if (onRefresh) {
        await onRefresh();
      }

      event.detail.complete();
    } catch {
      event.detail.complete();
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh]);

  // Memoized so this ~200-line grouping/sorting/render pass only re-runs when
  // the data it actually reads changes. Previously this was a plain inline
  // IIFE in the JSX below, which re-ran on EVERY render of Agenda for ANY
  // reason — including SheetModal's onClose -> setSelectedBooking(null),
  // which fires right as the sheet's drag-close animation finishes. That
  // re-render raced the animation's final paint for the main thread, showing
  // as a "pause" at the bottom of the sheet's close before it visually
  // finished disappearing off screen.
  // handleBookingClick/checkCompletionStatus are intentionally omitted from
  // deps: neither is wrapped in useCallback (so a literal dep would defeat
  // the memo every render since their identity changes every render anyway),
  // and both only close over already-tracked state (completionStatuses) or
  // stable setState functions.
  const agendaListContent = React.useMemo(() => {
    const selectedDateStr = getLocalDateString(selectedDate);
    const grouped: { [date: string]: BookingItem[] } = {};
    const allBookingDates = new Set(bookings.map(b => getLocalDateString(toDate(b.startTime))));

    allBookingDates.forEach(date => {
      if (!grouped[date]) grouped[date] = [];
    });

    if (visibleBookings && visibleBookings.length > 0) {
      visibleBookings.forEach((booking: BookingItem) => {
        const localDate = getLocalDateString(toDate(booking.startTime));
        if (!grouped[localDate]) grouped[localDate] = [];
        grouped[localDate].push(booking);
      });
    }

    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => {
        const aTime = toDate(a.startTime).getTime();
        const bTime = toDate(b.startTime).getTime();
        return aTime - bTime;
      });
    });

    const allDates = getSortedUniqueBookingDates(visibleBookings, true);
    let renderDates: string[] = [];

    // From the list's first day (the picked date) onward; earlier days appear once
    // the user scrolls up and they have been loaded (listFloor becomes null).
    renderDates = Array.from(new Set(allDates)).filter(date => listFloor === null || date >= listFloor).sort();

    // Always include selected date even if it has no bookings (shows "Nothing Planned")
    const datesToRender = Array.from(new Set([selectedDateStr, ...renderDates.filter(date => grouped[date]?.length > 0)])).sort();

    return [
      ...datesToRender.map(date => (
        <IonItemGroup key={`group-${date}`}>
          <IonItemDivider sticky id={`day-${date}`} data-date={date}>
            <IonLabel>
              <IonText className={`${styles.dateText} ${parseLocalDate(date).toDateString() === new Date().toDateString()
                ? styles.today
                : parseLocalDate(date).toDateString() === selectedDate.toDateString()
                  ? styles.selected
                  : styles.default
                }`}>
                {parseLocalDate(date).toDateString() === new Date().toDateString() && t('pages.bookingCalendar.Agenda.today')}
                {parseLocalDate(date).toLocaleDateString(intlLocale(), { weekday: 'long' })},
                {' '}
                {parseLocalDate(date).toLocaleDateString(intlLocale(), { month: 'short' })}
                {' '}
                <IonText>
                  {parseLocalDate(date).toLocaleDateString(intlLocale(), { day: '2-digit' })}
                </IonText>
              </IonText>
            </IonLabel>
          </IonItemDivider>
          <IonList>
            {(!grouped[date] || grouped[date].length === 0) && date === selectedDateStr && (
              <IonItem lines="none" className={styles.nothingScheduledItem}>
                <IonCol className={styles.nothingIconCol}>
                  <IonAvatar className={styles.nothingIcon}>
                    <IonIcon icon={calendarOutline} />
                  </IonAvatar>
                </IonCol>
                <IonCol className={styles.nothingContent}>
                  <IonLabel className={styles.nothingText}>{t('pages.bookingCalendar.Agenda.nothingScheduled')}</IonLabel>
                </IonCol>
              </IonItem>
            )}
            {(grouped[date] || []).map((booking: BookingItem, bookingIndex: number) => {
              const isBlocked = booking.type === 'blocked';
              const isOpaque = isBlocked && booking.transparency === 'OPAQUE';

              // Blocked time: the block's title on top, the staff member under it.
              const blockedStaffName = booking.staff
                || booking.resourceObj?.name
                || (booking.resource ? staffNameById.get(booking.resource) : undefined)
                || (booking.agendaItem?.scheduleId ? staffNameById.get(booking.agendaItem.scheduleId) : undefined)
                || (booking.agendaItem?.externalScheduleId ? staffNameById.get(booking.agendaItem.externalScheduleId) : undefined)
                || '';
              const blockedTitle = (() => {
                const titleWithoutTime = booking.title?.split('(')[0]?.trim();
                if (titleWithoutTime && titleWithoutTime !== 'Time Blocked' && titleWithoutTime !== 'Blocked Time') {
                  const parts = titleWithoutTime.split(' - ');
                  return parts.length > 1 ? (parts[1]?.trim() || titleWithoutTime) : titleWithoutTime;
                }
                return translateDynamic('Blocked Time');
              })();

              const displayTitle = isBlocked ? blockedTitle : (booking.contact ? booking.contact.name : t('pages.bookingCalendar.Agenda.client'));

              const displayServiceType = isBlocked ? (blockedStaffName ? t('pages.bookingCalendar.Agenda.with', { blockedStaffName }) : '') : translateDynamic(booking.serviceType);


              const serviceColor = isBlocked ? '#bbb' : serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || '#2196f3';

              return (
                <IonItem
                  key={`booking-${date}-${booking.id}-${bookingIndex}`}
                  onClick={() => {
                    if (isBlocked) {
                      setSelectedBlockedBooking(booking);
                      setIsBlockedFormOpen(true);
                      return;
                    }
                    handleBookingClick(booking, toDate(booking.startTime));
                  }}
                  lines="none"
                  button
                  detail={false}
                  aria-label={t('pages.bookingCalendar.Agenda.viewBookingOnAt', { displayServiceType, v2: toDate(booking.startTime).toLocaleDateString(intlLocale()), v3: toDate(booking.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
                  className={`${styles.bookingItem} ${completionStatus[booking.id] ? styles.completedbooking : ''} ${isBlocked ? styles.blockedSlot : ''} ${isOpaque ? styles.opaque : ''}`}
                  style={{ '--service-color': serviceColor } as React.CSSProperties}
                >
                  <IonCol className={styles.timeContainer}>
                    <IonLabel>
                      <IonText>
                        {booking.startTime instanceof Date && !isNaN(booking.startTime.getTime()) && booking.endTime instanceof Date && !isNaN(booking.endTime.getTime()) ? (
                          <>
                            <span>{booking.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            <br />
                            <span className={styles.timeDash}>–</span>
                            <br />
                            <span>{booking.endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </>
                        ) : t('pages.bookingCalendar.Agenda.timeNotSet')}
                      </IonText>
                    </IonLabel>
                  </IonCol>
                  <IonCol className={styles.contentCol}>
                    <IonRow className={`${styles.clientRow} ${isBlocked ? styles.blockedRow : ''}`} data-testid="client-row">
                      {!isBlocked && (
                        <IonAvatar className={styles.avatar}>
                          <img
                            src={avatarUrls[booking.id] || getDefaultAvatar('Client')}
                            alt={booking.contact?.name || t('pages.bookingCalendar.Agenda.client')}
                            loading="eager"
                            className={styles.avatarImg}
                          />
                        </IonAvatar>
                      )}
                      <IonText className={styles.clientName}>
                        {displayTitle}
                      </IonText>
                      {!isBlocked && (
                        <IonButton
                          fill="clear"
                          className={styles.moreButton}
                          aria-label={t('pages.bookingCalendar.Agenda.moreOptions')}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedBooking(booking);
                          }}
                        >
                          <IonIcon icon={ellipsisHorizontal} slot="icon-only" />
                        </IonButton>
                      )}
                    </IonRow>
                    <IonRow className={`${styles.serviceRow} ${isBlocked ? styles.blockedRow : ''}`} data-testid="service-row">
                      <IonText>{displayServiceType}</IonText>
                    </IonRow>
                    {!isBlocked && (booking.staff || booking.resource) && (
                      <IonRow className={`${styles.resourceRow} ${checkCompletionStatus(booking.id) ? styles.completedRow : ''}`} data-testid="resource-row">
                        <IonText>
                          {[booking.staff, booking.resource].filter(Boolean).join(' · ')}
                        </IonText>
                      </IonRow>
                    )}
                    {!isBlocked && (
                      <IonRow className={styles.locationRow} data-testid="location-row">
                        <IonIcon icon={locationSharp} />
                        <IonText >{booking.contact && booking.contact.address ? booking.contact.address : ''}</IonText>
                      </IonRow>
                    )}
                  </IonCol>
                </IonItem>
              );
            })}
          </IonList>
        </IonItemGroup>
      ))
    ];
  }, [
    selectedDate,
    listFloor,
    bookings,
    visibleBookings,
    avatarUrls,
    completionStatus,
    checkCompletionStatus,
    getSortedUniqueBookingDates,
    staffNameById, t
  ]);

  // Show skeleton if loading or refreshing or navigating date range
  // (Never while scrolling: that would swap the list out from under the user.)
  // The skeleton replaces the whole list, so it is only for loads the user asked for (a
  // picked date, a refresh, navigation) or when there is nothing to show yet. A fetch that
  // merely adds content while scrolling must never flash it.
  const showSkeleton =
    (isNavigatingDateRange || isRefreshing || (isLoading && (explicitLoad || visibleBookings.length === 0))) &&
    !isLoadingMore &&
    !isScrollActive;
  // The explicit load is over once loading stops (adjust-state-during-render pattern)
  if (!isLoading && explicitLoad) setExplicitLoad(false);

  useEffect(() => {
    if (triggerNavigationSkeletonRef) {
      triggerNavigationSkeletonRef.current = () => {
        setIsNavigatingDateRange(true);
      };
      return () => {
        triggerNavigationSkeletonRef.current = null;
      };
    }
  }, [triggerNavigationSkeletonRef]);

  // Clears the navigation skeleton once new bookings have actually loaded —
  // computed during render (React's "adjust state when a dependency
  // changes" pattern) rather than as a setState call inside an effect,
  // since it's a pure derivation from already-available state with no
  // external system involved.
  const [prevNavSkeletonInputs, setPrevNavSkeletonInputs] = useState({ isNavigatingDateRange, bookingsLength: bookings.length, isLoading, isRefreshing });
  if (
    prevNavSkeletonInputs.isNavigatingDateRange !== isNavigatingDateRange ||
    prevNavSkeletonInputs.bookingsLength !== bookings.length ||
    prevNavSkeletonInputs.isLoading !== isLoading ||
    prevNavSkeletonInputs.isRefreshing !== isRefreshing
  ) {
    setPrevNavSkeletonInputs({ isNavigatingDateRange, bookingsLength: bookings.length, isLoading, isRefreshing });
    if (isNavigatingDateRange && bookings.length > 0 && !isLoading && !isRefreshing) {
      setIsNavigatingDateRange(false);
    }
  }


  // Clean up any lingering barrier states on component unmount
  useEffect(() => {
    return () => {
      try {
        // Clean up barrier states if component unmounts with modal open
        let cleanupAttempts = 0;
        const cleanupBarrier = () => {
          if (window.history.state?.modalBarrier && cleanupAttempts < 5) {
            window.history.back();
            cleanupAttempts++;
            // Use setTimeout to allow history to settle
            setTimeout(cleanupBarrier, 10);
          }
        };
        cleanupBarrier();
      } catch (error) {
        console.warn('Failed to cleanup barrier states on unmount:', error);
      }
    };
  }, []);

  // Prevent browser back button from navigating when booking details modal is open
  useEffect(() => {
    if (!isBookingDetailsModalOpen) return;

    let hasAddedListener = false;

    const handlePopState = () => {
      console.log('BookingDetails: popstate event, isBookingModalOpen:', isBookingModalOpen);
      // Only handle if this is the topmost modal (i.e., booking modal is not open on top)
      if (!isBookingModalOpen) {
        // Prevent navigation and close modal
        setIsBookingDetailsModalOpen(false);
        setBookingDetailsModalId(undefined);
      }
    };

    // Only push barrier state once when modal opens
    const currentState = window.history.state;
    const needsBarrier = !currentState?.bookingDetailsBarrier;

    if (needsBarrier) {
      try {
        window.history.pushState({ ...currentState, bookingDetailsBarrier: true }, '');
      } catch (error) {
        console.error('Failed to push barrier state:', error);
      }
    }

    window.addEventListener('popstate', handlePopState);
    hasAddedListener = true;

    return () => {
      if (hasAddedListener) {
        window.removeEventListener('popstate', handlePopState);
      }
      // Only consume barrier if this modal is closing and booking modal is not open
      // Use a longer delay to ensure the booking modal has time to open if it's going to
      if (!isBookingModalOpen && needsBarrier) {
        setTimeout(() => {
          // Double check that booking modal didn't open in the meantime
          if (!isBookingModalOpenRef.current) {
            const state = window.history.state;
            if (state?.bookingDetailsBarrier) {
              try {
                console.log('BookingDetails: Consuming barrier on cleanup');
                window.history.back();
              } catch (error) {
                console.warn('Failed to consume barrier state:', error);
              }
            }
          } else {
            console.log('BookingDetails: Not consuming barrier, booking modal is open');
          }
        }, 200);
      }
    };
  }, [isBookingDetailsModalOpen, isBookingModalOpen]);

  // Prevent browser back button from navigating when booking modal is open
  useEffect(() => {
    if (!isBookingModalOpen) return;

    let hasAddedListener = false;

    const handlePopState = () => {
      console.log('Booking: popstate event, closing booking modal');
      // Booking modal is always on top, so it should always handle the back button when open
      setIsBookingModalOpen(false);
      setBookingModalId(undefined);
    };

    // Only push barrier state once when modal opens
    const currentState = window.history.state;
    const needsBarrier = !currentState?.bookingBarrier;

    if (needsBarrier) {
      try {
        window.history.pushState({ ...currentState, bookingBarrier: true }, '');
      } catch (error) {
        console.error('Failed to push barrier state:', error);
      }
    }

    window.addEventListener('popstate', handlePopState);
    hasAddedListener = true;

    return () => {
      if (hasAddedListener) {
        window.removeEventListener('popstate', handlePopState);
      }
      // Only consume barrier if we added it
      // IMPORTANT: When modal closes, check if BookingDetails is still open
      // If so, DON'T consume the barrier yet - let BookingDetails stay open
      if (needsBarrier) {
        setTimeout(() => {
          // Check if BookingDetails modal is still open
          if (isBookingDetailsModalOpenRef.current) {
            console.log('Booking: Not consuming barrier, BookingDetails is still open');
            return;
          }

          const state = window.history.state;
          if (state?.bookingBarrier) {
            try {
              console.log('Booking: Consuming barrier on cleanup');
              window.history.back();
            } catch (error) {
              console.warn('Failed to consume barrier state:', error);
            }
          }
        }, 50);
      }
    };
  }, [isBookingModalOpen]);

  return (
    <>
      <SheetModal
        isOpen={selectedBooking !== null}
        onClose={() => setSelectedBooking(null)}
        title={t('pages.bookingCalendar.Agenda.bookingOptions')}
        headerBadge={selectedBooking ? getBookingPaymentBadge(selectedBooking) : undefined}
        headerTitle={translateDynamic(selectedBooking?.serviceType)}
        headerSubtitle={selectedBooking ? formatBookingHeaderSubtitle(selectedBooking) : undefined}
        menuOptions={selectedBooking ? getbookingOptions(selectedBooking) : []}
      />

      <SheetModal
        isOpen={activeAlert === 'cancellation-options'}
        onClose={() => {
          setActiveAlert(null);
          closeOptionsModal();
          setFeeStep('selection');
          setFeeType(null);
          setCustomFeeAmount('');
        }}
        title={feeStep === 'selection' ? t('pages.bookingCalendar.Agenda.cancelBooking') : t('pages.bookingCalendar.Agenda.selectFeeType')}
        additionalContent={
          <div className={cancellationStyles.cancellationContainer}>
            {feeStep === 'selection' ? (
              <>
                {/* STEP 1: Fee Selection */}
                <div>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingCalendar.Agenda.cancellationFee')}</div>
                  <div className={cancellationStyles.feeOptionsSection}>
                    <label className={`${cancellationStyles.feeOption} ${waiveFee ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeOption"
                        checked={waiveFee}
                        onChange={() => setWaiveFee(true)}
                        aria-label={t('pages.bookingCalendar.Agenda.waiveCancellationFee')}
                      />
                      <div className={cancellationStyles.feeLabel}>
                        <span className={cancellationStyles.optionTitle}>{t('pages.bookingCalendar.Agenda.waiveCancellationFee')}</span>
                        <span className={cancellationStyles.optionDescription}>{t('pages.bookingCalendar.Agenda.noChargeToCustomer')}</span>
                      </div>
                    </label>

                    <label className={`${cancellationStyles.feeOption} ${!waiveFee ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeOption"
                        checked={!waiveFee}
                        onChange={() => setWaiveFee(false)}
                        aria-label={t('pages.bookingCalendar.Agenda.applyCancellationFee')}
                      />
                      <div className={cancellationStyles.feeLabel}>
                        <span className={cancellationStyles.optionTitle}>{t('pages.bookingCalendar.Agenda.applyCancellationFee')}</span>
                        <span className={cancellationStyles.optionDescription}>{t('pages.bookingCalendar.Agenda.perServicePolicy')}</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Notify Client */}
                <div className={cancellationStyles.notifySection}>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingCalendar.Agenda.notification')}</div>
                  <div className={cancellationStyles.notifyCheckbox}>
                    <input
                      type="checkbox"
                      id="notifyClient"
                      checked={notifyClient}
                      onChange={(e) => setNotifyClient(e.target.checked)}
                      aria-label={t('pages.bookingCalendar.Agenda.notifyClientViaEmail')}
                    />
                    <label htmlFor="notifyClient">{t('pages.bookingCalendar.Agenda.notifyClientViaEmail')}</label>
                  </div>
                </div>

                {/* Message */}
                {notifyClient && (
                  <div className={cancellationStyles.messageSection}>
                    <label htmlFor="cancellationMsg">{t('pages.bookingCalendar.Agenda.addMessageOptional')}</label>
                    <textarea
                      id="cancellationMsg"
                      value={cancellationMessage}
                      onChange={(e) => setCancellationMessage(e.target.value)}
                      placeholder={t('pages.bookingCalendar.Agenda.enterAMessageToSendTo')}
                      aria-label={t('pages.bookingCalendar.Agenda.cancellationMessage')}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className={cancellationStyles.actionButtons}>
                  <button
                    className={cancellationStyles.cancelBtn}
                    onClick={() => {
                      setActiveAlert(null);
                      setFeeStep('selection');
                    }}
                  >{t('pages.bookingCalendar.Agenda.cancel')}
                  </button>
                  <button
                    className={waiveFee ? cancellationStyles.confirmBtn : cancellationStyles.nextBtn}
                    onClick={() => {
                      console.log('Agenda Step 1 button clicked - waiveFee:', waiveFee, 'selectedBooking:', selectedBooking?.id);
                      if (waiveFee) {
                        if (selectedBooking) {
                          console.log('Waive fee selected, voiding Zoho SO then cancelling...');
                          const bookingIdToCancel = selectedBooking.id;
                          const finishCancel = () => {
                            DataStorageService.removeData(`booking-${bookingIdToCancel}`);
                            DataStorageService.storeData('booking-refresh-trigger', Date.now());
                            if (onRefresh) onRefresh();
                            setActiveAlert(null);
                            setSelectedBooking(null);
                            setFeeStep('selection');
                            setWaiveFee(true);
                            setNotifyClient(true);
                            setCancellationMessage('');
                          };
                          // Order: unattended (done inside cancelBooking) -> cancel in Wix -> take the job off its invoice
                          // and void its order in Zoho. If Wix refuses, nothing in Zoho is touched.
                          dataService.bookings.cancelBooking(bookingIdToCancel, waiveFee)
                            .then(async () => {
                              try {
                                await cancelServiceOrder(bookingIdToCancel);
                              } catch (zohoErr) {
                                // Wix is already cancelled; the cancel webhook retries the Zoho side and flags the booking.
                                console.error('[Agenda] Zoho SO void failed:', zohoErr);
                              }
                              finishCancel();
                            })
                            .catch((wixErr) => {
                              console.error('[Agenda] Wix cancel failed:', wixErr);
                              presentAlert({
                                header: t('pages.bookingCalendar.Agenda.serviceOrderError'),
                                message: t('pages.bookingSystem.BookingDetails.failedToCancelBooking'),
                                cssClass: 'logout-alert',
                                buttons: ['OK'],
                              });
                            });
                        } else {
                          console.warn('Waive fee but no selectedBooking');
                        }
                      } else {
                        console.log('Apply fee selected, moving to step 2');
                        setFeeStep('type');
                        setFeeType(null);
                      }
                    }}
                  >
                    {waiveFee ? t('pages.bookingCalendar.Agenda.confirmCancellation') : t('pages.bookingCalendar.Agenda.next')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* STEP 2: Fee Type Selection (only shown when applying fee) */}
                <div>
                  <div className={cancellationStyles.sectionTitle}>{t('pages.bookingCalendar.Agenda.feeType')}</div>
                  <div className={cancellationStyles.feeTypeSection}>
                    <label className={`${cancellationStyles.feeTypeOption} ${feeType === 'standard' ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeType"
                        checked={feeType === 'standard'}
                        onChange={() => {
                          setFeeType('standard');
                          setStandardPreset(null);
                          setCustomFeeAmount('');
                        }}
                        aria-label={t('pages.bookingCalendar.Agenda.standardCancellationFee')}
                      />
                      <div className={cancellationStyles.feeTypeLabel}>
                        <span className={cancellationStyles.feeTypeTitle}>{t('pages.bookingCalendar.Agenda.standardFee')}</span>
                        <span className={cancellationStyles.feeTypeDescription}>{t('pages.bookingCalendar.Agenda.useServiceSStandardCancellationFee')}</span>
                      </div>
                    </label>

                    <label className={`${cancellationStyles.feeTypeOption} ${feeType === 'custom' ? cancellationStyles.selected : ''}`}>
                      <input
                        type="radio"
                        name="feeType"
                        checked={feeType === 'custom'}
                        onChange={() => setFeeType('custom')}
                        aria-label={t('pages.bookingCalendar.Agenda.customCancellationFee')}
                      />
                      <div className={cancellationStyles.feeTypeLabel}>
                        <span className={cancellationStyles.feeTypeTitle}>{t('pages.bookingCalendar.Agenda.customFee')}</span>
                        <span className={cancellationStyles.feeTypeDescription}>{t('pages.bookingCalendar.Agenda.enterACustomAmount')}</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Standard Fee presets */}
                {feeType === 'standard' && (
                  <div className={cancellationStyles.feeTypeSection}>
                    {STANDARD_FEE_PRESETS.map(p => (
                      <label key={p.id} className={`${cancellationStyles.feeTypeOption} ${standardPreset === p.id ? cancellationStyles.selected : ''}`}>
                        <input
                          type="radio"
                          name="standardPreset"
                          checked={standardPreset === p.id}
                          onChange={() => setStandardPreset(p.id)}
                          aria-label={presetLabel(p.fee, jobPrice)}
                        />
                        <div className={cancellationStyles.feeTypeLabel}>
                          <span className={cancellationStyles.feeTypeTitle}>{presetLabel(p.fee, jobPrice)}</span>
                          <span className={cancellationStyles.feeTypeDescription}>{typeof p.fee === 'number' ? t('pages.bookingCalendar.Agenda.flatFee') : t('pages.bookingCalendar.Agenda.ofTheCleaningPrice')}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Custom Fee Input */}
                {feeType === 'custom' && (
                  <div className={cancellationStyles.customFeeInputSection}>
                    <label htmlFor="customFee">{t('pages.bookingCalendar.Agenda.customFeeAmount')}</label>
                    <input
                      id="customFee"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={t('pages.bookingCalendar.Agenda.enterAmount')}
                      value={customFeeAmount}
                      onChange={(e) => setCustomFeeAmount(e.target.value)}
                      aria-label={t('pages.bookingCalendar.Agenda.customFeeAmount2')}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className={cancellationStyles.actionButtons}>
                  <button
                    className={`${cancellationStyles.cancelBtn} ${cancellationStyles.backButton}`}
                    onClick={() => {
                      setFeeStep('selection');
                      setFeeType(null);
                      setCustomFeeAmount('');
                    }}
                  >{t('pages.bookingCalendar.Agenda.back')}
                  </button>
                  <button
                    className={cancellationStyles.confirmBtn}
                    onClick={() => {
                      console.log('Agenda Step 2 Confirm clicked - feeType:', feeType, 'selectedBooking:', selectedBooking?.id);
                      if (feeType && selectedBooking) {
                        console.log('Calling cancelBooking API with id:', selectedBooking.id, 'waiveFee:', waiveFee);
                        const bookingIdToCancel2 = selectedBooking.id;
                        const proceedWithCancel2 = async () => {
                          await dataService.bookings.cancelBooking(bookingIdToCancel2, waiveFee);
                          DataStorageService.removeData(`booking-${bookingIdToCancel2}`);
                          DataStorageService.storeData('booking-refresh-trigger', Date.now());
                          if (onRefresh) onRefresh();
                          setActiveAlert(null);
                          setSelectedBooking(null);
                          setFeeStep('selection');
                          setFeeType(null);
                          setCustomFeeAmount('');
                          setWaiveFee(true);
                          setNotifyClient(true);
                          setCancellationMessage('');
                        };
                        // Standard Fee has no defined amount yet (the real
                        // cancellation policy is judgment-based — $50-100 or
                        // 50%/100% of the cleaning fee depending on notice
                        // given and repeat-cancellation history, not a fixed
                        // formula) — falls back to voiding, same as Waive,
                        // until a real amount source exists. Custom Fee
                        // applies the owner-entered amount instead of voiding.
                        const zohoAction = feeType === 'custom'
                          ? applyCancellationFee(bookingIdToCancel2, parseFloat(customFeeAmount))
                          : applyCancellationFee(bookingIdToCancel2, presetFee(standardPreset!));
                        zohoAction
                          .then(() => proceedWithCancel2())
                          .catch((zohoErr) => {
                            console.error('[Agenda] Zoho SO update failed:', zohoErr);
                            presentAlert({
                              header: t('pages.bookingCalendar.Agenda.serviceOrderError'),
                              message: t('pages.bookingCalendar.Agenda.theCancellationFeeCouldNotBe'),
                              cssClass: 'logout-alert',
                              buttons: [
                                { text: t('pages.bookingCalendar.Agenda.cancel'), role: 'cancel' },
                                { text: t('pages.bookingCalendar.Agenda.continueAnyway'), handler: () => proceedWithCancel2().catch(e => console.error(e)) },
                              ],
                            });
                          });
                      } else {
                        console.warn('Step 2 Confirm - missing feeType or selectedBooking', { feeType, selectedBooking });
                      }
                    }}
                    disabled={!feeType || (feeType === 'custom' && !customFeeAmount) || (feeType === 'standard' && !standardPreset)}
                  >{t('pages.bookingCalendar.Agenda.confirmCancellation')}
                  </button>
                </div>
              </>
            )}
          </div>
        }
        menuOptions={[]}
      />

      <IonCard className={`${styles.agenda} ${isCalendarExpanded ? styles.expanded : ''}`}>
        <IonContent scrollY={false} className={styles.agendaContent}>
          <div ref={refresherAnchorRef} className={styles.refresherAnchor} />
          {refresherReady && (
            <PositionedRefresher
              titleRef={refresherAnchorRef}
              contentRef={scrollContainerRef}
              offset={0}
              id="agenda-refresher"
              onIonRefresh={handleRefresh}
            >
              <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
            </PositionedRefresher>
          )}
          <div
            ref={scrollContainerRef}
            className={`${styles.agendaScrollHost} ion-content-scroll-host`}
            onScroll={handleScroll}
          >
            {showSkeleton ? (
              <IonList className={styles.skeletonOverlay}>
                {Array.from({ length: 8 }, (_, index) => (
                  <IonItem key={index} lines="full">
                    <IonSkeletonText animated slot="start" style={{ width: '40px', height: '40px', borderRadius: '0%' }} />
                    <IonLabel>
                      <h3 className={styles.skeletonTitleRow}>
                        <IonSkeletonText animated style={{ width: '70%' }} />
                        {/* stands in for the horizontal "more options" dots, top right of the card */}
                        <IonSkeletonText animated className={styles.skeletonMoreDots} />
                      </h3>
                      <p><IonSkeletonText animated style={{ width: '50%' }} /></p>
                      <p><IonSkeletonText animated style={{ width: '40%' }} /></p>
                      <p><IonSkeletonText animated style={{ width: '30%' }} /></p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>
            ) : (
              <IonList>
                {agendaListContent}
                {/* The load-more indicator is a floating glass pill, portaled to the page: it takes no space in
                  the list (the old fixed 56px row left dead space under the last booking) and, living outside the
                  memoised list above, shows the moment a load starts instead of waiting for every booking row to be
                  rebuilt (1-2s on a phone). Always mounted so it can fade in and out. */}
                {onLoadNextDays && bookings.length > 0 && visibleBookings.length > 0 && createPortal(
                  <IonLabel
                    className={`${styles.loadMorePill} ${isLoadingMore && !noMoreBookings ? styles.loadMorePillVisible : ''}`}
                    role="status"
                    aria-live="polite"
                  >
                    <IonText className={styles.loadMoreText}>{t('pages.bookingCalendar.Agenda.loadingEvents')}</IonText>
                    <IonText className={styles.orbitSpinner} aria-hidden="true">
                      <IonText className={`${styles.orbitDot} ${styles.orbitDot1}`} />
                      <IonText className={`${styles.orbitDot} ${styles.orbitDot2}`} />
                      <IonText className={`${styles.orbitDot} ${styles.orbitDot3}`} />
                      <IonText className={`${styles.orbitDot} ${styles.orbitDot4}`} />
                    </IonText>
                  </IonLabel>,
                  document.body
                )}
              </IonList>
            )}
          </div>
        </IonContent>
      </IonCard>

      <Booking
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setBookingModalId(undefined);
        }}
        bookingId={bookingModalId}
      />

      <BookingDetailsModal
        isOpen={isBookingDetailsModalOpen}
        onClose={() => {
          setIsBookingDetailsModalOpen(false);
          setBookingDetailsModalId(undefined);
        }}
        bookingId={bookingDetailsModalId}
      />

      <BlockedTimeForm
        isOpen={isBlockedFormOpen}
        onClose={() => {
          setIsBlockedFormOpen(false);
          setSelectedBlockedBooking(null);
        }}
        initialBooking={selectedBlockedBooking}
        initialDate={selectedDate}
      />
    </>
  );
};

export default Agenda;
